import { GatsbyImage, getImage } from 'gatsby-plugin-image'
import { useEffect, useRef, useState } from 'react'
import styled, { css, keyframes } from 'styled-components'
import Chevron from '../assets/svg/chevron-right.svg'
import MagnifyIcon from '../assets/svg/magnifying-glass.svg'
import CrossIcon from '../assets/svg/plus.svg'
import { heading1Style } from '../utils/FontAndSpacingStyles'
import { useIntl } from '../utils/IntlContext'
import { createAppUrl, getLocalePathPrefix, urlPathJoin } from '../utils/locales'
import media from '../utils/media'
import CTA from './CTA'
import Link from './Link'
import Wrapper from './Wrapper'

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`

const overflowEllipsis = css`
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: max-content;
  white-space: nowrap;
`

const BannerWrapper = styled.div`
  position: relative;
  padding-top: var(--nav-header-height);

  ${media.md`
    ${Wrapper} {
      min-height: 675px;
    }
  `}
`

const BannerInnerWrapper = styled(Wrapper)`
  margin: 0 auto;
  padding: var(--spacing-md);
`

const BannerBackground = styled.div`
  background-color: var(--color-background-mint);
  width: 100%;
  position: absolute;
  height: ${({ $backgroundHeight }) => $backgroundHeight};
  left: 0;
  top: var(--nav-header-height);
  z-index: -1;

  ${media.md`
    height: ${({ $backgroundHeight }) => $backgroundHeight};
  `}
`

const BannerLink = styled(Link)`
  border-bottom: 2px solid var(--color-primary);
  padding-bottom: 2px;

  ${({ $showEllipsis }) => ($showEllipsis ? overflowEllipsis : '')}

  ${media.md`
    border-bottom: 3px solid var(--color-primary);
  `}
`

const CategoryLink = styled(Link)`
  ${overflowEllipsis}
  max-width: 110px;
  color: var(--color-primary);

  ${media.sm`
    overflow: visible;
  `}
`

const Divider = styled.div`
  &:after {
    content: '/';
    padding: 0 5px;
  }
`

const BannerLinkWrapper = styled.div`
  display: flex;
`

const SliderCardsWrapper = styled.div`
  flex: 1 1 408px;
  max-width: 297px;
  max-height: 408px;
  margin: 16px;

  ${media.md`
    max-width: 185px;
    max-height: 265px;
    flex: 1 1 0;
  `}
`

const SliderCards = styled(GatsbyImage)`
  cursor: ${({ $active }) => ($active ? 'default' : 'pointer')};
  border-radius: var(--border-radius-large);
  box-shadow: ${({ $active }) =>
    $active ? 'var(--box-shadow-primary-medium)' : 'var(--box-shadow-primary-small-with-glow)'};
  border: 2px solid ${({ $active }) => ($active ? 'var(--color-primary)' : 'transparent')};
`

const HighlightedResume = styled(GatsbyImage)`
  max-width: 456px;
  max-height: 626px;
  border-radius: var(--border-radius-large);
  box-shadow: var(--box-shadow-primary-medium-with-larger-darker-glow);
  overflow: hidden;
`

const chevronStyles = css`
  top: 0;
  bottom: 0;
  margin: auto;
  height: 28px;
  width: 28px;
  padding: 7px;
  background: var(--color-background-primary);
  border-radius: 50%;
  position: absolute;
  cursor: pointer;
  z-index: 1;

  g {
    fill: var(--color-typography-on-primary);
  }
`

const LeftChevron = styled(Chevron)`
  left: 5px;
  transform: rotate(180deg);
  ${chevronStyles}
`

const RightChevron = styled(Chevron)`
  right: 5px;
  ${chevronStyles}
`

const ResumeCarouselWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
`

const BannerTitle = styled.h1`
  ${heading1Style}
  margin-bottom: 16px;
`

const BannerBody = styled.div`
  font-size: 16px;
  margin-bottom: 24px;

  a {
    color: var(--color-primary);
  }

  ${media.md`
    font-size: 18px;
  `}
`

const CopyControllerWrapper = styled.div`
  width: 100%;
  padding-top: 44px;
  text-align: center;

  ${media.md`
    width: 50%;
    text-align: initial;
  `}
`

const HighlightedResumeWrapper = styled.div`
  width: 40%;
  position: relative;
  display: none;
  height: max-content;

  ${media.md`
    display: block;
  `}
`

const UseSample = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  margin: auto;
  height: max-content;
  text-align: center;
`

const CarouselController = styled.div`
  position: relative;
  overflow: hidden;
  margin-left: -25px;
  width: 100vw;

  ${media.md`
    margin-left: unset;
    width: unset;
  `}
`

const CardsWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-around;
  transition: all 0.5s;
  width: max-content;
  margin-left: 25px;
  transform: translateX(-${({ $sliderOffset }) => $sliderOffset}%);

  ${media.md`
    width: ${({ $widthMultiplier }) => 100 + $widthMultiplier}%;
    margin-left: unset;
  `}
`

const DotsWrapper = styled.div`
  text-align: center;
  margin-top: 16px;
  display: block;

  ${media.md`
    display: none;
  `}
`

const Dot = styled.span`
  &:before {
    content: '•';
    padding: 5px;
    color: ${({ $active }) => ($active ? 'var(--color-primary)' : 'var(--color-lighter-grey)')};
    font-size: 25px;
    cursor: ${({ $active }) => ($active ? 'default' : 'pointer')};
  }
`

const StyledMagnifyIcon = styled(MagnifyIcon)`
  box-shadow: var(--box-shadow-primary-medium-with-larger-darker-glow);
  background-color: var(--color-background-white);
  border-radius: 50%;
  width: 40px;
  height: 40px;
  padding: 8px;
  position: absolute;
  z-index: 1;
  left: -20px;
  top: 22px;
  cursor: pointer;
  transition: all 0.5s;

  &:hover {
    transform: scale(1.2);
  }
`

const ModalMask = styled.div`
  position: fixed;
  z-index: 10;
  overflow: scroll;
  width: 100vw;
  height: 100vh;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  animation: ${fadeIn} 0.3s ease-in;
  opacity: 1;
`

const ModalContentWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  background: #183b5b91;
`

const StyledCrossIcon = styled(CrossIcon)`
  position: fixed;
  right: 30px;
  top: 30px;
  transform: rotate(45deg);
  cursor: pointer;
  z-index: 1;

  g {
    stroke: white;
  }
`
const ModalResume = styled(GatsbyImage)`
  width: 60vw;
  max-width: 790px;
  margin: auto;
  margin-top: 105px;
  margin-bottom: 100px;
  border-radius: var(--border-radius-large);
`

const ModalCopyBar = styled.div`
  position: fixed;
  background: var(--color-background-mint);
  left: 0;
  right: 0;
  bottom: 16px;
  max-width: 985px;
  width: 80vw;
  margin: auto;
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  padding: 20px 24px;
  border-radius: 5px;
  box-shadow: var(--box-shadow-primary-medium-with-larger-darker-glow);
`

const ModalCopyTitle = styled.h3`
  margin-bottom: 5px;
  font-size: 24px;
  font-weight: 500;
  line-height: 1;
`

const ModalCopyBody = styled.p`
  margin-bottom: unset;
`

const ModalCopyButton = styled(CTA)`
  height: min-content;
  margin: auto 0;
`

const ModalBackground = styled.div`
  width: 100%;
  height: 100%;
  position: fixed;
`

const MobileSampleButton = styled(CTA)`
  && {
    font-size: 14px;
    font-weight: 600;
    padding: 16px 33px;
    margin-bottom: 32px;

    ${media.md`
      display: none;
    `}
  }
`

const AboveFoldContent = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  min-height: calc(100vh - calc(var(--nav-header-height) + 50px));

  ${media.sm`
    display: block;
    align-items: initial;
    min-height: unset;
  `}
`

const SampleExampleBanner = ({
  resumes,
  activeIndex,
  setActiveIndex,
  signupLink,
  name,
  h1Title,
  introduction,
  parentBasePath,
  slug,
  isResumeSample,
}) => {
  const [currentOffset, setCurrentOffset] = useState(0)
  const [backgroundHeight, setBackgroundHeight] = useState('50%')
  const [touchStart, setTouchStart] = useState(0)
  const [touchEnd, setTouchEnd] = useState(0)
  const [modalActive, setModalActive] = useState(false)

  const carouselRef = useRef(null)

  const { locale, t } = useIntl()

  const highlightedResume = resumes[activeIndex]
  const widthMultiplier = resumes.length > 2 ? (resumes.length - 3) * 33 : 0

  const resizeBackground = () => {
    const height = `max(${carouselRef.current?.getBoundingClientRect().top + window.scrollY}px, 50%)`
    setBackgroundHeight(height)
  }

  const handleResumeChange = (index) => {
    setActiveIndex(index)
  }

  const offsetIndexUpdate = (index) => {
    setCurrentOffset(index)
    setActiveIndex(index)
  }

  useEffect(() => {
    if (typeof window === 'undefined') {
      return
    }

    resizeBackground()
    window.addEventListener('resize', resizeBackground)

    const removeResizeListener = () => {
      window.removeEventListener('resize', resizeBackground)
    }

    return removeResizeListener
  }, [])

  const changeScrollOffset = (newOffset, mobileOffset) => {
    if (
      (newOffset >= resumes.length - 2 && !mobileOffset) ||
      (newOffset >= resumes.length && mobileOffset) ||
      newOffset <= 0
    ) {
      offsetIndexUpdate(0)
    } else {
      offsetIndexUpdate(newOffset)
    }
  }

  const handleSwipe = (e) => {
    if (e.type === 'touchstart') {
      setTouchStart(e.targetTouches[0].clientX)
    }

    if (e.type === 'touchmove') {
      setTouchEnd(e.targetTouches[0].clientX)
    }

    if (e.type === 'touchend' && touchStart - touchEnd > 75) {
      changeScrollOffset(currentOffset + 1, true)
    }

    if (e.type === 'touchend' && touchStart - touchEnd < -75) {
      changeScrollOffset(currentOffset - 1)
    }
  }

  return (
    <BannerWrapper>
      <BannerBackground $backgroundHeight={backgroundHeight} />
      {modalActive && (
        <ModalMask>
          <ModalContentWrapper>
            <StyledCrossIcon onClick={() => setModalActive(false)} />
            <ModalBackground onClick={() => setModalActive(false)} />
            <ModalResume
              image={getImage(highlightedResume.image)}
              alt={
                isResumeSample
                  ? `${highlightedResume.title} Resume Sample and Template`
                  : t('example_image_alt', { name: highlightedResume.title })
              }
              title={
                isResumeSample
                  ? `Best ${highlightedResume.title} Resume Sample and Template for ${new Date().getFullYear()}`
                  : t('example_image_title', { name: highlightedResume.title, year: new Date().getFullYear() })
              }
            />
            <ModalCopyBar>
              <div>
                <ModalCopyTitle>{t('sample_modal_title')}</ModalCopyTitle>
                <ModalCopyBody>{t('sample_modal_body', { title: name })}</ModalCopyBody>
              </div>
              {highlightedResume.sample && isResumeSample ? (
                <ModalCopyButton
                  $primary
                  to={createAppUrl(`/?sample=${highlightedResume.slug}`, locale)}
                  rel="nofollow">
                  {t('use_this_sample')}
                </ModalCopyButton>
              ) : (
                <ModalCopyButton
                  $primary
                  to={signupLink}>
                  {t('signup')}
                </ModalCopyButton>
              )}
            </ModalCopyBar>
          </ModalContentWrapper>
        </ModalMask>
      )}
      <BannerInnerWrapper>
        <BannerLinkWrapper>
          <BannerLink to={getLocalePathPrefix(locale)}>{t('homepage')}</BannerLink>
          <Divider />
          <BannerLink
            $showEllipsis
            to={parentBasePath}>
            {isResumeSample ? t('resume_samples') : t('cv_examples')}
          </BannerLink>
          <Divider />
          <CategoryLink to={urlPathJoin(parentBasePath, slug)}>{name}</CategoryLink>
        </BannerLinkWrapper>
        <ResumeCarouselWrapper>
          <CopyControllerWrapper>
            <AboveFoldContent>
              <BannerTitle>
                {isResumeSample
                  ? h1Title || t('title_resume_samples', { title: name })
                  : h1Title || t('example_header', { occupation: name })}
              </BannerTitle>
              {introduction ? (
                <div dangerouslySetInnerHTML={{ __html: introduction }} />
              ) : isResumeSample ? (
                <BannerBody>
                  <p>
                    {`This page provides you with ${name} resume samples to use to create your own resume with our easy-to-use `}
                    <Link to="/resume-builder/">resume builder</Link>
                    {`. Below you'll find our how-to section that will guide you through each section of a ${name} resume.`}
                  </p>
                </BannerBody>
              ) : (
                <BannerBody>
                  <p>{t('example_subheading', { occupation: name })}</p>
                  <p>{t('examples_banner_2', { occupation: name })}</p>
                </BannerBody>
              )}
              {highlightedResume.sample && isResumeSample ? (
                <MobileSampleButton
                  $primary
                  to={createAppUrl(`/?sample=${highlightedResume.slug}`, locale)}
                  rel="nofollow">
                  {t('use_this_sample')}
                </MobileSampleButton>
              ) : (
                <MobileSampleButton
                  $primary
                  to={signupLink}>
                  {t('signup')}
                </MobileSampleButton>
              )}
            </AboveFoldContent>
            <CarouselController>
              {resumes.length > 3 && (
                <>
                  <LeftChevron onClick={() => changeScrollOffset(currentOffset - 1)} />
                  <RightChevron onClick={() => changeScrollOffset(currentOffset + 1)} />
                </>
              )}
              <CardsWrapper
                ref={carouselRef}
                $widthMultiplier={widthMultiplier}
                $sliderOffset={currentOffset * (100 / resumes.length)}
                onTouchStart={(e) => handleSwipe(e)}
                onTouchMove={(e) => handleSwipe(e)}
                onTouchEnd={(e) => handleSwipe(e)}>
                {resumes.map((resume, index) => (
                  <SliderCardsWrapper
                    key={index}
                    onClick={() => handleResumeChange(index)}>
                    <SliderCards
                      $active={activeIndex === index}
                      image={getImage(resume.thumbnail)}
                      alt={
                        isResumeSample
                          ? `${resume.title} Resume Sample and Template`
                          : t('example_image_alt', { name: resume.title })
                      }
                      title={
                        isResumeSample
                          ? `Best ${resume.title} Resume Sample and Template for ${new Date().getFullYear()}`
                          : t('example_image_title', { name: resume.title, year: new Date().getFullYear() })
                      }
                    />
                  </SliderCardsWrapper>
                ))}
              </CardsWrapper>
            </CarouselController>
            <DotsWrapper>
              {resumes.map((resume, index) => (
                <Dot
                  $active={activeIndex === index}
                  key={index}
                  onClick={() => offsetIndexUpdate(index)}
                />
              ))}
            </DotsWrapper>
          </CopyControllerWrapper>
          <HighlightedResumeWrapper>
            <StyledMagnifyIcon onClick={() => setModalActive(true)} />
            <HighlightedResume
              image={getImage(highlightedResume.image)}
              alt={
                isResumeSample
                  ? `${highlightedResume.title} Resume Sample and Template`
                  : t('example_image_alt', { name: highlightedResume.title })
              }
              title={
                isResumeSample
                  ? `Best ${highlightedResume.title} Resume Sample and Template for ${new Date().getFullYear()}`
                  : t('example_image_title', { name: highlightedResume.title, year: new Date().getFullYear() })
              }
            />
            <UseSample>
              {highlightedResume.sample && isResumeSample ? (
                <CTA
                  $primary
                  to={createAppUrl(`/?sample=${highlightedResume.slug}`, locale)}
                  rel="nofollow">
                  {t('use_this_sample')}
                </CTA>
              ) : (
                <CTA
                  $primary
                  to={signupLink}>
                  {t('signup')}
                </CTA>
              )}
            </UseSample>
          </HighlightedResumeWrapper>
        </ResumeCarouselWrapper>
      </BannerInnerWrapper>
    </BannerWrapper>
  )
}

export default SampleExampleBanner
